import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  environment,
  resolveLocalBaseUrl,
  resolveLocalPath,
} from '@g99-product-web-ui/shared-environment';
import { Observable } from 'rxjs';

import { ServiceType } from '../../constants/service-types.enum';
import { HttpRequestOptions } from '../../interfaces/http-request.interface';
import { TenantService } from '../../services/helper-service/tenant.service';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  // Default base URL
  private readonly _defaultBaseUrl = environment.local.apiUrls.default;

  // Map of service types to base URLs
  private readonly _baseUrls: Record<string, string> = {
    [ServiceType.DEFAULT]: this._defaultBaseUrl,
    [ServiceType.AUTH]:
      environment.local?.apiUrls.authApiUrl || this._defaultBaseUrl,
    [ServiceType.PUBLIC]:
      environment.local?.apiUrls.public || this._defaultBaseUrl,
    [ServiceType.AI_SERVICE]:
      environment.local?.apiUrls.aiServiceApiUrl || this._defaultBaseUrl,
    [ServiceType.SCORECARD]:
      environment.local?.apiUrls.scorecardApiUrl || this._defaultBaseUrl,
  };

  /**
   * Constructs an instance of HttpService.
   *
   * @param _http - The HttpClient instance used to make HTTP requests.
   * @param _tenantService - The TenantService instance used to handle tenant-related operations.
   */
  constructor(
    private readonly _http: HttpClient,
    private readonly _tenantService: TenantService
  ) {}

  public get<T>(
    url: string,
    options: HttpRequestOptions = {
      attachTenantId: true,
      responseType: 'json',
    },
    serviceType: ServiceType = ServiceType.DEFAULT
  ): Observable<T> {
    // Prepare headers with tenant ID if required
    const headers = this._prepareHeaders(options);

    const baseUrl = this._getBaseUrl(serviceType);
    return this._http.get<T>(baseUrl + resolveLocalPath(serviceType, url), {
      headers,
      params: options.params,
      responseType: options.responseType as 'json',
      context: options.context,
    });
  }

  public post<T, B>(
    url: string,
    body: B,
    options: {
      params?: HttpParams;
      headers?: HttpHeaders;
      attachTenantId: boolean;
      responseType?: 'json' | 'text' | 'blob' | 'arraybuffer';
      context?: HttpContext; // ✅ Add context option
      accept?: string;
      base64EncodeTenantId?: boolean;
    } = { attachTenantId: true, responseType: 'json' },
    serviceType: ServiceType = ServiceType.DEFAULT
  ): Observable<T> {
    // Prepare headers with tenant ID if required
    const headers = this._prepareHeaders(options);
    const baseUrl = this._getBaseUrl(serviceType);

    return this._http.post<T>(
      baseUrl + resolveLocalPath(serviceType, url),
      body,
      {
        headers,
        params: options.params,
        responseType: options.responseType as 'json',
        context: options.context, // ✅ Pass context through
      }
    );
  }

  public put<T, B>(
    url: string,
    body: B,
    options: {
      params?: HttpParams;
      headers?: HttpHeaders;
      attachTenantId: boolean;
      responseType?: 'json' | 'text' | 'blob' | 'arraybuffer';
      context?: HttpContext;
      accept?: string;
      base64EncodeTenantId?: boolean;
    } = { attachTenantId: true, responseType: 'json' },
    serviceType: ServiceType = ServiceType.DEFAULT
  ): Observable<T> {
    // Prepare headers with tenant ID if required
    const headers = this._prepareHeaders(options);
    const baseUrl = this._getBaseUrl(serviceType);

    return this._http.put<T>(
      baseUrl + resolveLocalPath(serviceType, url),
      body,
      {
        headers,
        params: options.params,
        responseType: options.responseType as 'json',
        context: options.context,
      }
    );
  }
  public patch<T, B>(
    url: string,
    body: B,
    options: {
      params?: HttpParams;
      headers?: HttpHeaders;
      attachTenantId: boolean;
      accept?: string;
      base64EncodeTenantId?: boolean;
    } = { attachTenantId: true },
    serviceType: ServiceType = ServiceType.DEFAULT
  ): Observable<T> {
    // Prepare headers with tenant ID if required
    const headers = this._prepareHeaders(options);

    const baseUrl = this._getBaseUrl(serviceType);

    return this._http.patch<T>(
      baseUrl + resolveLocalPath(serviceType, url),
      body,
      {
        headers,
        params: options.params,
      }
    );
  }
  public delete<T, B = unknown>(
    url: string,
    body?: B,
    options: {
      params?: HttpParams;
      headers?: HttpHeaders;
      attachTenantId?: boolean;
      accept?: string;
      base64EncodeTenantId?: boolean;
    } = { attachTenantId: true },
    serviceType: ServiceType = ServiceType.DEFAULT
  ): Observable<T> {
    // Prepare headers with tenant ID if required
    const headers = this._prepareHeaders(options);

    const baseUrl = this._getBaseUrl(serviceType);

    // Build HTTP options. If a body is provided, include it.
    const httpOptions: {
      headers: HttpHeaders;
      params?: HttpParams;
      body?: B;
    } = {
      headers,
      params: options.params,
    };

    if (body !== undefined) {
      httpOptions.body = body;
    }

    return this._http.delete<T>(
      baseUrl + resolveLocalPath(serviceType, url),
      httpOptions
    );
  }

  /**
   * Builds an instance of `HttpParams` from a given parameter object.
   *
   * The `buildHttpParams` utility function safely constructs query parameters for HTTP requests.
   * It handles both scalar values and arrays:
   * - For array values, each item is appended individually with the same key.
   * - For scalar values (strings, numbers, booleans), they are stringified and set directly.
   *
   * Null or undefined values are automatically ignored to prevent unwanted query parameters.
   *
   * @template T - A generic type that extends `Record<string, unknown>`, ensuring type safety.
   * @param {T} params - An object containing the key-value pairs to be serialized into query parameters.
   * @returns {HttpParams} An `HttpParams` instance populated with the serialized parameters.
   *
   * @example
   * const params = buildHttpParams({
   *   stage: 'lead',
   *   status: ['active', 'new'],
   *   tags: ['tag1', 'tag2']
   * });
   *
   * // Resulting URL query: ?stage=lead&status=active&status=new&tags=tag1&tags=tag2
   */
  public buildHttpParams<T extends Record<string, unknown>>(
    params: T,
    joinWithComma = true
  ): HttpParams {
    return Object.entries(params).reduce((httpParams, [key, value]) => {
      if (Array.isArray(value)) {
        if (joinWithComma) {
          const joinedValue = value.map(String).join(',');
          httpParams = httpParams.set(key, joinedValue);
        } else {
          value.forEach((val) => {
            httpParams = httpParams.append(key, String(val));
          });
        }
      } else if (value !== undefined && value !== null) {
        httpParams = httpParams.set(key, String(value));
      }
      return httpParams;
    }, new HttpParams());
  }

  private _prepareHeaders(options: {
    headers?: HttpHeaders;
    attachTenantId?: boolean;
    accept?: string;
    base64EncodeTenantId?: boolean;
  }): HttpHeaders {
    let headers = options.headers || new HttpHeaders();
    if (options.accept) {
      headers = headers.set('Accept', options.accept);
    }

    headers = this._tenantService._attachTenantId(
      headers,
      options.attachTenantId ?? true
    );

    if (options.base64EncodeTenantId && headers.has('X-TenantID')) {
      const tenantId = headers.get('X-TenantID');
      if (tenantId) {
        headers = headers.set('X-TenantID', btoa(tenantId));
      }
    }

    return headers;
  }

  /**
   * Retrieves the appropriate base URL based on the service type
   *
   * The `ServiceType` enum is used to determine which base URL to return.
   * If the `serviceType` parameter is not provided, the default base URL is returned.
   * If the `serviceType` parameter is provided, the corresponding base URL is returned.
   * If the provided `serviceType` is not recognized, the default base URL is returned.
   *
   * @param {ServiceType} [serviceType=ServiceType.DEFAULT] - The type of service (e.g., AUTH, PUBLIC)
   * @returns {string} The base URL for the specified service
   */
  private _getBaseUrl(serviceType: ServiceType = ServiceType.DEFAULT): string {
    const fallback = this._baseUrls[serviceType] || this._defaultBaseUrl;
    // local-api.config.ts can redirect a service to localhost while testing; when its
    // master switch is off (default) this returns the normal dev/prod base URL unchanged.
    return resolveLocalBaseUrl(serviceType, fallback);
  }

  // You can add similar methods for PUT, DELETE, PATCH, etc.
}
